import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Switch } from '../ui/Switch';
import { 
  Zap, 
  Save, 
  X, 
  Plus,
  Trash2,
  AlertCircle,
  Clock,
  Calendar,
  Target,
  Settings,
  PlayCircle,
  PauseCircle,
  Repeat,
  Activity
} from 'lucide-react';
import toast from 'react-hot-toast';

const AddAutomationModal = ({ isOpen, onClose, automation, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    trigger: 'schedule',
    scheduleType: 'interval',
    interval: '5',
    intervalUnit: 'minutes',
    cronExpression: '',
    timezone: 'UTC',
    conditions: [],
    actions: [],
    enabled: true,
    priority: 'medium',
    retryOnFailure: true,
    maxRetries: 3,
    timeout: '300',
    notifications: {
      onSuccess: false,
      onFailure: true,
      onWarning: false
    },
    tags: [],
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [newCondition, setNewCondition] = useState({ field: '', operator: 'equals', value: '' });
  const [newAction, setNewAction] = useState({ type: '', config: '' });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (automation) {
        setFormData({
          name: automation.name || '',
          description: automation.description || '',
          status: automation.status || 'active',
          trigger: automation.trigger || 'schedule',
          scheduleType: automation.scheduleType || 'interval',
          interval: automation.interval || '5',
          intervalUnit: automation.intervalUnit || 'minutes',
          cronExpression: automation.cronExpression || '',
          timezone: automation.timezone || 'UTC',
          conditions: automation.conditions || [],
          actions: automation.actions || [],
          enabled: automation.enabled !== undefined ? automation.enabled : true,
          priority: automation.priority || 'medium',
          retryOnFailure: automation.retryOnFailure !== undefined ? automation.retryOnFailure : true,
          maxRetries: automation.maxRetries || 3,
          timeout: automation.timeout || '300',
          notifications: automation.notifications || {
            onSuccess: false,
            onFailure: true,
            onWarning: false
          },
          tags: automation.tags || [],
          notes: automation.notes || ''
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, automation]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      status: 'active',
      trigger: 'schedule',
      scheduleType: 'interval',
      interval: '5',
      intervalUnit: 'minutes',
      cronExpression: '',
      timezone: 'UTC',
      conditions: [],
      actions: [],
      enabled: true,
      priority: 'medium',
      retryOnFailure: true,
      maxRetries: 3,
      timeout: '300',
      notifications: {
        onSuccess: false,
        onFailure: true,
        onWarning: false
      },
      tags: [],
      notes: ''
    });
    setErrors({});
    setNewCondition({ field: '', operator: 'equals', value: '' });
    setNewAction({ type: '', config: '' });
    setNewTag('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Automation name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.trigger === 'schedule') {
      if (formData.scheduleType === 'interval') {
        if (!formData.interval || isNaN(formData.interval) || parseInt(formData.interval) <= 0) {
          newErrors.interval = 'Interval must be a positive number';
        }
      } else if (formData.scheduleType === 'cron') {
        if (!formData.cronExpression.trim()) {
          newErrors.cronExpression = 'Cron expression is required';
        }
      }
    }

    if (formData.actions.length === 0) {
      newErrors.actions = 'At least one action is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const automationData = {
        ...formData,
        interval: formData.scheduleType === 'interval' ? parseInt(formData.interval) : null,
        timeout: parseInt(formData.timeout),
        maxRetries: parseInt(formData.maxRetries)
      };

      await onSave(automationData);
      toast.success(automation ? 'Automation updated successfully' : 'Automation created successfully');
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save automation');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCondition = () => {
    if (newCondition.field && newCondition.value) {
      setFormData(prev => ({
        ...prev,
        conditions: [...prev.conditions, { ...newCondition }]
      }));
      setNewCondition({ field: '', operator: 'equals', value: '' });
    }
  };

  const handleRemoveCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const handleAddAction = () => {
    if (newAction.type && newAction.config) {
      setFormData(prev => ({
        ...prev,
        actions: [...prev.actions, { ...newAction }]
      }));
      setNewAction({ type: '', config: '' });
    }
  };

  const handleRemoveAction = (index) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const triggerTypes = [
    { value: 'schedule', label: 'Scheduled' },
    { value: 'event', label: 'Event-based' },
    { value: 'webhook', label: 'Webhook' },
    { value: 'manual', label: 'Manual' }
  ];

  const actionTypes = [
    'Assign Ticket',
    'Send Notification',
    'Update Status',
    'Create Ticket',
    'Run Script',
    'Call API',
    'Update Custom Field',
    'Escalate Ticket'
  ];

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'in', label: 'In' }
  ];

  const conditionFields = [
    'Ticket Status',
    'Ticket Priority',
    'Ticket Category',
    'Ticket Department',
    'Ticket Age',
    'Agent Availability',
    'SLA Status'
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={automation ? 'Edit Automation' : 'Create New Automation'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
        {/* Basic Information */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <Zap className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Automation Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Auto-Assign Tickets"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={errors.name ? 'border-red-500' : ''}
                required
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Describe what this automation does..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className={errors.description ? 'border-red-500' : ''}
              required
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between pt-6">
              <Label htmlFor="enabled">Enabled</Label>
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enabled: checked }))}
              />
            </div>
          </div>
        </div>

        {/* Trigger Configuration */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <PlayCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Trigger Configuration</h3>
          </div>

          <div className="space-y-2">
            <Label htmlFor="trigger">Trigger Type</Label>
            <Select value={formData.trigger} onValueChange={(value) => setFormData(prev => ({ ...prev, trigger: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {triggerTypes.map((trigger) => (
                  <SelectItem key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.trigger === 'schedule' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="scheduleType">Schedule Type</Label>
                <Select value={formData.scheduleType} onValueChange={(value) => setFormData(prev => ({ ...prev, scheduleType: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interval">Interval</SelectItem>
                    <SelectItem value="cron">Cron Expression</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.scheduleType === 'interval' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="interval">
                      Interval <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="interval"
                      type="number"
                      min="1"
                      placeholder="5"
                      value={formData.interval}
                      onChange={(e) => setFormData(prev => ({ ...prev, interval: e.target.value }))}
                      className={errors.interval ? 'border-red-500' : ''}
                      required
                    />
                    {errors.interval && (
                      <p className="text-sm text-red-500">{errors.interval}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="intervalUnit">Unit</Label>
                    <Select value={formData.intervalUnit} onValueChange={(value) => setFormData(prev => ({ ...prev, intervalUnit: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minutes">Minutes</SelectItem>
                        <SelectItem value="hours">Hours</SelectItem>
                        <SelectItem value="days">Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="cronExpression">
                    Cron Expression <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="cronExpression"
                    placeholder="0 */5 * * * * (every 5 minutes)"
                    value={formData.cronExpression}
                    onChange={(e) => setFormData(prev => ({ ...prev, cronExpression: e.target.value }))}
                    className={errors.cronExpression ? 'border-red-500' : ''}
                    required
                  />
                  {errors.cronExpression && (
                    <p className="text-sm text-red-500">{errors.cronExpression}</p>
                  )}
                  <p className="text-xs text-gray-500">Format: second minute hour day month day-of-week</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={formData.timezone} onValueChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        {/* Conditions */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Conditions</h3>
          </div>

          {formData.conditions.length > 0 && (
            <div className="space-y-2">
              {formData.conditions.map((condition, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Badge variant="outline">{condition.field}</Badge>
                  <Badge variant="secondary">{condition.operator}</Badge>
                  <Badge variant="outline">{condition.value}</Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCondition(index)}
                    className="ml-auto"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-4 gap-2">
            <Select value={newCondition.field} onValueChange={(value) => setNewCondition(prev => ({ ...prev, field: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Field" />
              </SelectTrigger>
              <SelectContent>
                {conditionFields.map((field) => (
                  <SelectItem key={field} value={field}>
                    {field}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={newCondition.operator} onValueChange={(value) => setNewCondition(prev => ({ ...prev, operator: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Operator" />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op) => (
                  <SelectItem key={op.value} value={op.value}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              placeholder="Value"
              value={newCondition.value}
              onChange={(e) => setNewCondition(prev => ({ ...prev, value: e.target.value }))}
            />

            <Button type="button" onClick={handleAddCondition} variant="outline">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <Activity className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Actions <span className="text-red-500">*</span>
            </h3>
          </div>

          {errors.actions && (
            <p className="text-sm text-red-500">{errors.actions}</p>
          )}

          {formData.actions.length > 0 && (
            <div className="space-y-2">
              {formData.actions.map((action, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Badge variant="default">{action.type}</Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400 flex-1">{action.config}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAction(index)}
                    className="ml-auto"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            <Select value={newAction.type} onValueChange={(value) => setNewAction(prev => ({ ...prev, type: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Action Type" />
              </SelectTrigger>
              <SelectContent>
                {actionTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              placeholder="Configuration"
              value={newAction.config}
              onChange={(e) => setNewAction(prev => ({ ...prev, config: e.target.value }))}
            />

            <Button type="button" onClick={handleAddAction} variant="outline">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <Settings className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Advanced Settings</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="timeout">Timeout (seconds)</Label>
              <Input
                id="timeout"
                type="number"
                min="1"
                value={formData.timeout}
                onChange={(e) => setFormData(prev => ({ ...prev, timeout: e.target.value }))}
              />
            </div>

            <div className="flex items-center justify-between pt-6">
              <Label htmlFor="retryOnFailure">Retry on Failure</Label>
              <Switch
                id="retryOnFailure"
                checked={formData.retryOnFailure}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, retryOnFailure: checked }))}
              />
            </div>
          </div>

          {formData.retryOnFailure && (
            <div className="space-y-2">
              <Label htmlFor="maxRetries">Max Retries</Label>
              <Input
                id="maxRetries"
                type="number"
                min="1"
                max="10"
                value={formData.maxRetries}
                onChange={(e) => setFormData(prev => ({ ...prev, maxRetries: e.target.value }))}
              />
            </div>
          )}

          <div className="space-y-3">
            <Label>Notifications</Label>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="notifySuccess" className="font-normal">On Success</Label>
                <Switch
                  id="notifySuccess"
                  checked={formData.notifications.onSuccess}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, onSuccess: checked }
                  }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="notifyFailure" className="font-normal">On Failure</Label>
                <Switch
                  id="notifyFailure"
                  checked={formData.notifications.onFailure}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, onFailure: checked }
                  }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="notifyWarning" className="font-normal">On Warning</Label>
                <Switch
                  id="notifyWarning"
                  checked={formData.notifications.onWarning}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, onWarning: checked }
                  }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tags & Notes</h3>
          </div>

          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2 flex-wrap mb-2">
              {formData.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-red-500"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                Add
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Additional notes about the automation..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} loading={loading}>
            <Save className="h-4 w-4" />
            {automation ? 'Update Automation' : 'Create Automation'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddAutomationModal;

